import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, Link2, Share2, Ticket, X } from "lucide-react";
import { toast } from "sonner";
import { createInvite, getMyInvites, revokeInvite } from "@/lib/api";

const EXPIRY = [[1, "1 day"], [7, "7 days"], [30, "30 days"], [null, "Never"]];
const USES = [[1, "1 person"], [5, "5 people"], [25, "25 people"], [null, "No limit"]];
const STATE_LABEL = { revoked: "Revoked", expired: "Expired", used_up: "Used" };

export const inviteLink = (code) => `${window.location.origin}/invite/${code}`;
const prettyCode = (code) => `${code.slice(0, 4)}-${code.slice(4)}`;

function Choice({ options, value, onChange, label }) {
  return (
    <div>
      <div className="mb-1.5 text-[10px] uppercase tracking-[0.16em] text-[#A1A1AA]">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map(([option, text]) => (
          <button key={text} type="button" onClick={() => onChange(option)}
            className={`rounded-md border px-2.5 py-1.5 text-xs transition-colors ${value === option ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F5F5F0]" : "border-white/10 text-[#A1A1AA] hover:text-[#F5F5F0]"}`}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

// Bring-your-own-partner: share a code or link; whoever accepts it starts a Starter Sprint with you.
export default function InvitePartner({ compact = false }) {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [expires, setExpires] = useState(7);
  const [uses, setUses] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState("");
  const [showOld, setShowOld] = useState(false);

  useEffect(() => {
    // Links such as /matches#invite land here; the router doesn't scroll to hashes itself.
    if (window.location.hash === "#invite") document.getElementById("invite")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    let active = true;
    getMyInvites().then((result) => { if (active) setInvites(result.items || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      const invite = await createInvite({ expiresInDays: expires, maxUses: uses, note: note.trim() || null });
      setInvites((list) => [invite, ...list]);
      setNote("");
      await copy(invite, "link");
    } catch (error) {
      toast("Could not create an invite.", { description: error.message });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (invite, what) => {
    const text = what === "code" ? invite.code : inviteLink(invite.code);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(`${invite.id}:${what}`);
      toast(what === "code" ? "Invite code copied." : "Invite link copied.", { description: "Send it to the person you want to partner with." });
      window.setTimeout(() => setCopied(""), 2000);
    } catch {
      toast(text);
    }
  };

  const share = async (invite) => {
    const text = `Be my accountability partner on HustlrMate. Accept here: ${inviteLink(invite.code)} (or enter code ${invite.code} in the app)`;
    if (navigator.share) {
      try { await navigator.share({ title: "HustlrMate partner invite", text, url: inviteLink(invite.code) }); return; }
      catch { /* dismissed: fall back to copying */ }
    }
    try { await navigator.clipboard.writeText(text); toast("Invite message copied."); } catch { toast(text); }
  };

  const revoke = async (invite) => {
    try {
      const updated = await revokeInvite(invite.id);
      setInvites((list) => list.map((item) => item.id === invite.id ? updated : item));
    } catch (error) {
      toast("Could not revoke the invite.", { description: error.message });
    }
  };

  const openCode = (event) => {
    event.preventDefault();
    const clean = code.trim().split("/").pop().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (clean.length < 6) { toast("Enter the 8-character invite code."); return; }
    navigate(`/invite/${clean}`);
  };

  const live = invites.filter((invite) => invite.state === "live");
  const old = invites.filter((invite) => invite.state !== "live");

  return (
    <section id="invite" className="hm-card rounded-md p-6" data-testid="invite-partner">
      <div className="flex items-start gap-3">
        <Ticket className="mt-0.5 h-5 w-5 shrink-0 text-[#F59E0B]" strokeWidth={1.5} />
        <div>
          <div className="overline mb-1">Bring your own partner</div>
          <h2 className="font-serif-editorial text-2xl font-light leading-tight">Already know who should hold you to it?</h2>
          {!compact && <p className="mt-2 max-w-2xl text-sm text-[#A1A1AA]">Send them an invite. When they accept, you skip matching and start a seven-day Starter Sprint together. Invites don't use up your plan's matches.</p>}
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-6">
            <Choice label="Expires after" options={EXPIRY} value={expires} onChange={setExpires} />
            <Choice label="Can be used by" options={USES} value={uses} onChange={setUses} />
          </div>
          <label className="block text-xs text-[#A1A1AA]">
            A line for them <span className="text-[#777]">· optional</span>
            <input value={note} maxLength={160} onChange={(event) => setNote(event.target.value)} placeholder="Let's both finish the GATE syllabus by March." className="hm-input mt-1 w-full rounded-md px-3 py-2 text-sm" />
          </label>
          <button onClick={create} disabled={busy} data-testid="invite-create" className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
            <Link2 className="h-4 w-4" /> {busy ? "Creating…" : "Create invite link"}
          </button>
        </div>
        <form onSubmit={openCode} className="rounded-md border border-white/10 bg-black/20 p-4">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#A1A1AA]">Got a code?</div>
          <input value={code} onChange={(event) => setCode(event.target.value)} placeholder="ABCD-2345" aria-label="Invite code" className="hm-input mt-2 w-full rounded-md px-3 py-2 font-mono text-sm uppercase tracking-[0.2em]" />
          <button className="hm-btn-ghost mt-3 w-full rounded-md px-3 py-2 text-xs">Open invite</button>
        </form>
      </div>

      {live.length > 0 && (
        <ul className="mt-6 divide-y divide-white/5 border-t border-white/5">
          {live.map((invite) => (
            <li key={invite.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="font-mono text-base tracking-[0.2em] text-[#F5F5F0]">{prettyCode(invite.code)}</div>
                <div className="text-xs text-[#A1A1AA]">
                  {invite.maxUses ? `${invite.uses} of ${invite.maxUses} used` : `${invite.uses} used`} · {invite.expiresAt ? `expires ${new Date(invite.expiresAt).toLocaleDateString()}` : "never expires"}
                  {invite.note ? ` · “${invite.note}”` : ""}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => copy(invite, "link")} className="hm-btn-ghost inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs">{copied === `${invite.id}:link` ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Link</button>
                <button onClick={() => copy(invite, "code")} className="hm-btn-ghost inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs">{copied === `${invite.id}:code` ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Code</button>
                <button onClick={() => share(invite)} className="hm-btn-ghost inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs"><Share2 className="h-3.5 w-3.5" /> Share</button>
                <button onClick={() => revoke(invite)} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-[#A1A1AA] hover:text-[#F5F5F0]" aria-label={`Revoke invite ${invite.code}`}><X className="h-3.5 w-3.5" /> Revoke</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {old.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowOld((value) => !value)} className="text-xs text-[#A1A1AA] underline-offset-4 hover:underline">{showOld ? "Hide" : "Show"} {old.length} past invite{old.length === 1 ? "" : "s"}</button>
          {showOld && (
            <ul className="mt-2 space-y-1">
              {old.map((invite) => (
                <li key={invite.id} className="flex justify-between text-xs text-[#7c7c84]">
                  <span className="font-mono tracking-[0.2em]">{prettyCode(invite.code)}</span>
                  <span>{STATE_LABEL[invite.state]} · {invite.uses} used</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
